'use client';

import { useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { DBListing } from '@/lib/services/dataService';

type ListingEvent = 'INSERT' | 'UPDATE' | 'DELETE';

interface UseRealtimeListingsOptions {
  /** Filter by owner_id — pass undefined to subscribe to all listings (admin) */
  ownerId?: string;
  /** Only receive events for these statuses (UPDATE only) */
  statusFilter?: DBListing['status'][];
  onInsert?: (listing: DBListing) => void;
  onUpdate?: (listing: DBListing) => void;
  onDelete?: (id: string) => void;
}

/**
 * Subscribes to real-time changes on the `listings` table.
 * Automatically cleans up the channel on unmount.
 */
export function useRealtimeListings({
  ownerId,
  statusFilter,
  onInsert,
  onUpdate,
  onDelete,
}: UseRealtimeListingsOptions) {
  // Keep callbacks in refs so the subscription closure never goes stale
  const onInsertRef = useRef(onInsert);
  const onUpdateRef = useRef(onUpdate);
  const onDeleteRef = useRef(onDelete);
  onInsertRef.current = onInsert;
  onUpdateRef.current = onUpdate;
  onDeleteRef.current = onDelete;

  useEffect(() => {
    const supabase = createClient();
    const channelName = ownerId
      ? `listings:owner:${ownerId}`
      : 'listings:admin';

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'listings',
          ...(ownerId ? { filter: `owner_id=eq.${ownerId}` } : {}),
        },
        (payload) => {
          const eventType = payload.eventType as ListingEvent;

          if (eventType === 'INSERT') {
            const newListing = payload.new as DBListing;
            if (statusFilter && !statusFilter.includes(newListing.status)) return;
            onInsertRef.current?.(newListing);
          } else if (eventType === 'UPDATE') {
            const updated = payload.new as DBListing;
            if (statusFilter && !statusFilter.includes(updated.status)) return;
            onUpdateRef.current?.(updated);
          } else if (eventType === 'DELETE') {
            const deletedId = (payload.old as { id: string }).id;
            onDeleteRef.current?.(deletedId);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [ownerId]); // re-subscribe only if ownerId changes
}
